/**
 * Advisory save warnings (DEV-16944). A successful `PATCH /v1/workflows/:id`
 * (or `POST .../versions`) may carry a top-level `warnings` array — e.g. an
 * llm-agent node referencing a `toolRef` that matches neither an MCP
 * connection nor a platform tool id. The save still committed; these are
 * yellow "heads up" lines, NOT blocking validation errors (which arrive via
 * a 4xx and render in the red SaveErrorAlert instead).
 *
 * Pure helpers live here (no React) so both the editor context and the
 * banner component share one parse/format path — and so the node-env test
 * suite can exercise them without mounting the editor.
 */

import type { ToolRefWarning } from '@cortex/types';

/**
 * One save warning. The shape is `ToolRefWarning` from `@cortex/types` (the
 * cross-package contract for save-time tool-ref advisories) with `kind`
 * loosened to `string`: the API only mints `unknown_tool_ref` today, but the
 * banner must tolerate warning kinds newer than this console build instead of
 * dropping them.
 */
export type ISaveWarning = Omit<ToolRefWarning, 'kind'> & { kind: string };

/**
 * Defensively pull `warnings` off a save response. The API only includes the
 * field when non-empty, and older API builds omit it entirely — both cases
 * (plus any malformed entry) collapse to `[]` so the console works against
 * every server version.
 */
export function extractSaveWarnings(res: unknown): ISaveWarning[] {
  if (!res || typeof res !== 'object') return [];
  const raw = (res as { warnings?: unknown }).warnings;
  if (!Array.isArray(raw)) return [];
  const out: ISaveWarning[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const w = entry as Record<string, unknown>;
    if (typeof w.message !== 'string' || w.message.length === 0) continue;
    out.push({
      message: w.message,
      ref: typeof w.ref === 'string' ? w.ref : '',
      kind: typeof w.kind === 'string' ? w.kind : 'unknown',
      ...(typeof w.nodeKey === 'string' && w.nodeKey.length > 0 ? { nodeKey: w.nodeKey } : {}),
    });
  }
  return out;
}

/** One display line per warning: "[node <nodeKey>] <message>", or the bare message. */
export function formatSaveWarning(w: ISaveWarning): string {
  return w.nodeKey ? `[node ${w.nodeKey}] ${w.message}` : w.message;
}
